/**
 * VenueBookingFlow - Calendar-based venue booking with request approval
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { 
  Calendar as CalendarIcon, Clock, Users, Check, ChevronRight, Loader2,
  Building, MapPin, Car, Utensils, Wine, AlertTriangle
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';
import AvailabilityConflictAlert from './AvailabilityConflictAlert';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_OPTIONS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', 
  '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'
];

const EVENT_TYPES = [
  { value: 'weddings', label: 'Wedding' },
  { value: 'birthday', label: 'Birthday Party' },
  { value: 'corporate', label: 'Corporate Event' },
  { value: 'religious', label: 'Religious/Cultural' },
  { value: 'other', label: 'Other Event' }
];

export default function VenueBookingFlow({ open, onClose, venue, onBookingComplete }) {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [eventType, setEventType] = useState('');
  const [guestCount, setGuestCount] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [notes, setNotes] = useState('');
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityConflict, setAvailabilityConflict] = useState(null);

  const addOns = venue?.add_ons || [];
  const venueDetails = venue?.venue_details || {};
  const currency = venue?.currency || 'Rs';

  // Check availability when date/time changes
  useEffect(() => {
    if (selectedDate && startTime && endTime && venue?.id) {
      checkAvailability();
    }
  }, [selectedDate, startTime, endTime]);

  const checkAvailability = async () => {
    if (!selectedDate || !startTime || !endTime) return;
    
    setCheckingAvailability(true);
    setAvailabilityConflict(null);
    
    try {
      const response = await fetch(`${API_URL}/api/availability/events/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_id: venue.id,
          event_date: format(selectedDate, 'yyyy-MM-dd'),
          start_time: startTime,
          end_time: endTime
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.has_conflict) {
          setAvailabilityConflict(result);
        }
      }
    } catch (error) {
      console.error('Availability check failed:', error);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setStartTime(slot.start);
    setEndTime(slot.end);
    setAvailabilityConflict(null);
    toast.success(`Selected ${slot.start} - ${slot.end}`);
  };

  const calculateTotal = () => {
    let total = 0;
    const selectedPackage = venue?.fixed_price_services?.find(p => 
      p.duration?.includes(startTime && endTime ? 
        Math.abs(parseInt(endTime) - parseInt(startTime)) + ' hours' : ''));
    
    if (selectedPackage) {
      total = selectedPackage.price;
    } else if (venue?.fixed_price_services?.[0]) {
      total = venue.fixed_price_services[0].price;
    }
    
    selectedAddOns.forEach(aoId => {
      const addOn = addOns.find(a => a.id === aoId);
      if (addOn) total += addOn.price;
    });
    
    return total;
  };

  const handleSubmit = async () => {
    if (availabilityConflict) {
      toast.error('Please select an available time slot');
      return;
    }
    
    setLoading(true);
    try {
      const bookingData = {
        venue_id: venue.id,
        event_date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: startTime,
        end_time: endTime,
        event_type: eventType,
        guest_count: parseInt(guestCount),
        add_ons: selectedAddOns,
        notes: notes,
        contact: contactInfo
      };

      const response = await fetch(`${API_URL}/api/events/bookings/venue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      const result = await response.json();
      
      if (response.ok) {
        if (result.conflict) {
          // Handle conflict returned from backend
          setAvailabilityConflict(result);
          toast.error(result.message);
        } else {
          toast.success('Venue booking request submitted! You\'ll hear back within 24 hours.');
          onBookingComplete?.();
          onClose();
        }
      } else {
        throw new Error(result.detail || 'Booking failed');
      }
    } catch (error) {
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Select Date & Time</h3>
            
            <div className="flex gap-4">
              <div className="flex-1">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date() || date > addDays(new Date(), 180)}
                  className="rounded-md border"
                />
              </div>
              
              <div className="flex-1 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Start Time</label>
                  <Select value={startTime} onValueChange={setStartTime}>
                    <SelectTrigger>
                      <Clock className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Select start time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">End Time</label>
                  <Select value={endTime} onValueChange={setEndTime}>
                    <SelectTrigger>
                      <Clock className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Select end time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.filter(t => t > startTime).map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedDate && startTime && endTime && (
                  <>
                    {checkingAvailability ? (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                        <span className="text-sm text-gray-600">Checking availability...</span>
                      </div>
                    ) : availabilityConflict ? (
                      <AvailabilityConflictAlert 
                        conflict={availabilityConflict}
                        onSelectSlot={handleSlotSelect}
                        onRefresh={checkAvailability}
                      />
                    ) : (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-800 flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          This slot is available!
                        </p>
                        <p className="text-sm text-green-700 mt-1">
                          <CalendarIcon className="w-4 h-4 inline mr-2" />
                          {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                        </p>
                        <p className="text-sm text-green-700">
                          <Clock className="w-4 h-4 inline mr-2" />
                          {startTime} - {endTime}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Event Details</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Event Type</label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Number of Guests</label>
                <Input
                  type="number"
                  value={guestCount}
                  onChange={(e) => setGuestCount(e.target.value)}
                  placeholder="Expected guests"
                  max={venueDetails.capacity}
                />
                {venueDetails.capacity && (
                  <p className="text-xs text-gray-500 mt-1">Max capacity: {venueDetails.capacity}</p>
                )}
              </div>
            </div>

            {/* Venue Features */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Venue Features</p>
              <div className="flex flex-wrap gap-2">
                {venueDetails.indoor && <Badge variant="outline"><Building className="w-3 h-3 mr-1" /> Indoor</Badge>}
                {venueDetails.outdoor && <Badge variant="outline"><Building className="w-3 h-3 mr-1" /> Outdoor</Badge>}
                {venueDetails.parking && <Badge variant="outline"><Car className="w-3 h-3 mr-1" /> Parking</Badge>}
                {venueDetails.catering_allowed && <Badge variant="outline"><Utensils className="w-3 h-3 mr-1" /> Catering OK</Badge>}
                {venueDetails.alcohol_allowed && <Badge variant="outline"><Wine className="w-3 h-3 mr-1" /> Alcohol OK</Badge>}
              </div>
            </div>

            {/* Add-ons */}
            {addOns.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Add-ons (Optional)</label>
                <div className="space-y-2">
                  {addOns.map(addOn => (
                    <label key={addOn.id} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={selectedAddOns.includes(addOn.id)}
                        onCheckedChange={(checked) => {
                          setSelectedAddOns(prev => 
                            checked 
                              ? [...prev, addOn.id]
                              : prev.filter(id => id !== addOn.id)
                          );
                        }}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{addOn.name}</p>
                      </div>
                      <p className="font-bold text-purple-600">{currency} {addOn.price?.toLocaleString()}</p>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Special Requests</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special requirements or requests..."
                rows={3}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Contact Information</h3>
            
            <div className="space-y-3">
              <Input
                placeholder="Your full name *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
              />
              <Input
                placeholder="Phone number *"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
              />
              <Input
                type="email"
                placeholder="Email address *"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium">Booking Summary</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span>{selectedDate && format(selectedDate, 'MMM d, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time</span>
                  <span>{startTime} - {endTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Event Type</span>
                  <span className="capitalize">{eventType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Guests</span>
                  <span>{guestCount}</span>
                </div>
                {selectedAddOns.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Add-ons</span>
                    <span>{selectedAddOns.length} selected</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                  <span>Estimated Total</span>
                  <span className="text-purple-600">{currency} {calculateTotal().toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                This is a booking request. The venue will review and confirm within 24 hours.
                A 20% deposit will be required upon confirmation.
              </p>
            </div>
          </div>
        );
    }
  };

  if (!venue) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="w-5 h-5 text-purple-600" />
            Book Venue
          </DialogTitle>
          <DialogDescription>{venue.provider_name}</DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step ? 'bg-purple-600 text-white' :
                s === step ? 'bg-purple-100 text-purple-700 border-2 border-purple-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`flex-1 h-1 ${s < step ? 'bg-purple-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {renderStep()}

        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>
          ) : <div />}
          
          {step < 3 ? (
            <Button 
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && (!selectedDate || !startTime || !endTime || checkingAvailability || availabilityConflict)) ||
                (step === 2 && (!eventType || !guestCount))
              }
              className="bg-purple-600 hover:bg-purple-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={loading || !contactInfo.name || !contactInfo.phone || !contactInfo.email}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Send Booking Request
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
